import { css } from "@compiled/react";
import { resolveFieldPath } from "src/uniform/components/filterable-gallery/src/utils/filterEngine";
import { useFilterableItem } from "src/uniform/contexts/filterable-item-context";

import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";

export interface FilterableCardProps {
    templateIdPath?: string;
    namePath?: string;
    urlSlugPath?: string;
    previewBlurbPath?: string;
    thumbnailPath?: string;
}

const DEFAULT_PATHS: Required<FilterableCardProps> = {
    templateIdPath: "sys.id",
    namePath: "fields.templateNameExternal",
    urlSlugPath: "fields.fullUrlSlug",
    previewBlurbPath: "fields.previewBlurb",
    thumbnailPath: "fields.templateThumbnail.fields.file.url"
};

const resolveString = (item: unknown, path: string): string | undefined => {
    const val = resolveFieldPath(item, path);
    return typeof val === "string" ? val : undefined;
};

const normaliseImageUrl = (url: string): string =>
    url.startsWith("//") ? `https:${url}` : url;

const FilterableCard = (props: FilterableCardProps): JSX.Element => {
    const item = useFilterableItem();
    const paths = { ...DEFAULT_PATHS, ...props };

    const name = resolveString(item, paths.namePath);
    const urlSlug = resolveString(item, paths.urlSlugPath);
    const blurb = resolveString(item, paths.previewBlurbPath);
    const rawThumbnail = resolveString(item, paths.thumbnailPath);
    const thumbnail = rawThumbnail
        ? normaliseImageUrl(rawThumbnail)
        : undefined;

    return (
        <div css={cardStyle} data-testid="filterable-card">
            {thumbnail && (
                <div css={thumbnailWrapperStyle}>
                    <Image
                        src={thumbnail}
                        alt={name ?? ""}
                        css={thumbnailStyle}
                        data-testid="card-thumbnail"
                    />
                </div>
            )}
            <div css={bodyStyle}>
                {name && (
                    <h2 className="card-title-sm" css={titleStyle}>
                        {name}
                    </h2>
                )}
                {blurb && <p css={blurbStyle}>{blurb}</p>}
                {urlSlug && name && (
                    <a
                        css={ctaStyle}
                        href={urlSlug}
                        aria-label={`Use ${name} template`}
                    >
                        Use template
                    </a>
                )}
            </div>
        </div>
    );
};

const cardStyle = css({
    "display": "flex",
    "flexDirection": "column",
    "width": "368px",
    "borderRadius": "6px",
    "boxShadow": token("elevation.shadow.raised"),
    "overflow": "hidden",
    "backgroundColor": token("elevation.surface"),
    "transition": "box-shadow 0.15s ease",
    ":hover": {
        boxShadow: token("elevation.shadow.overlay")
    }
});

const thumbnailWrapperStyle = css({
    width: "100%",
    aspectRatio: "321 / 210",
    overflow: "hidden",
    backgroundColor: token("color.background.neutral")
});

const thumbnailStyle = css({
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block"
});

const bodyStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    padding: token("space.300"),
    flexGrow: 1
});

const titleStyle = css({
    margin: 0
});

const blurbStyle = css({
    margin: 0,
    color: token("color.text.subtle"),
    fontSize: "0.875rem",
    lineHeight: "1.5"
});

const ctaStyle = css({
    "marginBlockStart": "auto",
    "paddingBlockStart": token("space.100"),
    "color": token("color.link"),
    "fontSize": "0.875rem",
    "fontWeight": 600,
    "textDecoration": "none",
    ":hover": {
        textDecoration: "underline"
    }
});

export default FilterableCard;
